import React from "react";
import { useFragment } from "react-relay";
import { graphql } from "react-relay";

import { useFeatureIsOn } from "@growthbook/growthbook-react";

import ResolvedModal from "~/stream/Comments/ResolvedModal";
import type { DeletionType } from "~/stream/DeleteNoteModal/DeleteNoteModal";
import { useModals } from "~/ui/modals";

import { MeetingPhaseContextProvider } from "../stream/MeetingPhaseContext";
import NoteHeader from "../stream/NoteHeader/NoteHeader";
import { TranscriptModalContext } from "./TranscriptModalContext";

import type { MeetingPhaseContext_viewer$key } from "~/__generated__/MeetingPhaseContext_viewer.graphql";
import type { NoteHeader_viewer$key } from "~/__generated__/NoteHeader_viewer.graphql";
import type { TranscriptModalHeader_note$key } from "~/__generated__/TranscriptModalHeader_note.graphql";

export function TranscriptModalHeader({
	noteHeaderNoteKey,
	viewer,
	closeModal,
	recordingId,
	onDeleteExecuted,
}: {
	noteHeaderNoteKey: TranscriptModalHeader_note$key | null;
	viewer: NoteHeader_viewer$key & MeetingPhaseContext_viewer$key;
	closeModal: () => void;
	recordingId?: string;
	onDeleteExecuted: (type: DeletionType) => void;
}) {
	const note = useFragment(
		graphql`
			fragment TranscriptModalHeader_note on Note {
				id
				isLocked
				canUnlockNote
				noteuserSet {
					id
					lastSeenAt
					user {
						__typename
						id
						type
						email
						firstName
						fullName
						image
						isActive
						isConfigured
						isGuest
						isTopUser
						isExternal

						internalAttributes {
							invitable
						}
					}
				}
				...NoteHeader_note
				...MeetingPhaseContext_note
			}
		`,
		noteHeaderNoteKey,
	);

	const { attendeesPresenceInfo, anonymousAttendees } = React.useContext(TranscriptModalContext);
	const modals = useModals();

	const openResolvedCommentsModal = React.useCallback(() => {
		if (!note) return;

		modals.open(({ close }) => <ResolvedModal noteId={note.id} onClose={close} />);
	}, [modals, note]);

	const recapCommentsFeatureIsEnabled = useFeatureIsOn("recap-comments");

	if (!note || !note.noteuserSet) {
		return <></>;
	}

	const attendeesForHeader = note.noteuserSet.map(noteuser => ({
		user: noteuser.user,
		lastSeenAt: noteuser.lastSeenAt,
		invitable: false,
		...noteuser.user,
	}));

	return note && attendeesForHeader.length > 0 ? (
		<MeetingPhaseContextProvider viewer={viewer} note={note}>
			<NoteHeader
				viewerKey={viewer}
				noteKey={note}
				inAgendaBuilderMode={false}
				attendees={attendeesForHeader}
				anonymousAttendees={anonymousAttendees}
				attendeesPresenceInfo={attendeesPresenceInfo}
				inMeetingModal
				closeMeetingModal={closeModal}
				allowCarryForward={false}
				recordingId={recordingId}
				onDeleteExecuted={onDeleteExecuted}
				onResolvedCommentsClick={recapCommentsFeatureIsEnabled ? openResolvedCommentsModal : undefined}
			/>
		</MeetingPhaseContextProvider>
	) : (
		<></>
	);
}
